import { Stack } from "@mui/material";
import { SendbirdGroupChannel } from "./chat.types";
import { isDefined } from "@clipboard-health/util-ts";
import { getChannelShiftTime, getChannelTitle, getLastMessageInfo } from "./channel";
import { Text } from "@clipboard-health/ui-react";
import { useChatWorkerQualifications } from "./useChatWorkerQualifications";
import { StarProfessionalTag } from "../facilityDashboard/ShiftCalendar/dayView/facilityShiftCard/starProfessionalTag";
import { PlacementChannelStatusChip } from "./components/PlacementChannelStatusChip";
import { PRIMARY_COLOR } from "../../appV2/lib/Theme/constants";
import { getLastMessageDate } from "../../utils/sendbird";
import { useActiveChatWindowDays } from "./useActiveChatWindowDays";
import { usePlacementApplicationStatus } from "@src/appV2/PlacementApplications/hooks/usePlacementApplicationStatus";
import { PlacementApplicationStatus } from "@src/appV2/PlacementApplications/types/fetchPlacementApplications.schema";
import "./chatStyle.scss";

interface ChannelPreviewProps {
  channel: SendbirdGroupChannel;
  isSelected: boolean;
  userType: string;
  currentUserId?: string;
  currentChannelUrl: string;
  isFirst: boolean;
  isLast: boolean;
  customTypesFilter: string;
  workplaceStarredProfessionalWorkerIds: string[];
  handleClick: (newChannel: string) => void;
  isAdmin?: boolean;
}

export function ChannelPreview(props: ChannelPreviewProps) {
  const {
    channel,
    handleClick,
    isSelected,
    userType,
    isFirst,
    isLast,
    currentUserId,
    customTypesFilter,
    workplaceStarredProfessionalWorkerIds,
    isAdmin = false,
  } = props;

  const channelTitle = getChannelTitle(channel, userType);

  const [workplaceId, workerId] = channel.url.split("_");

  const activeChatWindowDays = useActiveChatWindowDays();
  const { defaultWorkerQualification } = useChatWorkerQualifications({
    workerId,
    workplaceId,
    currentChannel: channel,
  });

  const isPlacementChannel = channel.customType === "placements";
  const { status: placementStatus } = usePlacementApplicationStatus({
    channelUrl: isPlacementChannel ? channel.url : undefined,
  });

  // Hide placement channels with interview:pending status from non-admin users
  if (
    !isAdmin &&
    isPlacementChannel &&
    placementStatus === PlacementApplicationStatus.INTERVIEW_PENDING
  ) {
    return null;
  }

  const hasUnreadMessages = channel.unreadMessageCount > 0;

  const classes = ["channel"];

  if (isSelected) {
    classes.push("selected");
  }

  if (!isFirst) {
    classes.push("border");
  }

  if (isLast) {
    classes.push("shadow");
  }

  if (channel?.metadata?.hcpName) {
    const worker = channel.members.find((member) => member.nickname === channel?.metadata?.hcpName);
    if (
      isDefined(worker?.userId) &&
      workplaceStarredProfessionalWorkerIds.includes(worker?.userId)
    ) {
      classes.push("starred");
    }
  }

  if (customTypesFilter === "starred" && !classes.includes("starred")) {
    return null;
  }

  return (
    <div
      tabIndex={0}
      role="row"
      key={channel.url}
      className={classes.join(" ")}
      onClick={() => handleClick(channel.url)}
      onKeyUp={() => handleClick(channel.url)}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        className="channel-name"
        sx={{ overflow: "hidden" }}
      >
        <Text
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {isDefined(defaultWorkerQualification)
            ? `${channelTitle}, ${defaultWorkerQualification}`
            : channelTitle}
        </Text>

        {isPlacementChannel && <PlacementChannelStatusChip channelUrl={channel.url} />}

        {classes.includes("starred") && (
          <StarProfessionalTag
            showText={false}
            backgroundColor={PRIMARY_COLOR}
            sx={{ paddingX: 0.25, paddingY: 0.25 }}
          />
        )}
      </Stack>

      <div className="channel-last">{getLastMessageDate(channel)}</div>

      <div className="channel-message">{getLastMessageInfo(channel, currentUserId ?? "")}</div>

      {hasUnreadMessages ? (
        <div className="channel-unread">{channel.unreadMessageCount}</div>
      ) : (
        // Empty div is needed to preserve grid layout
        <div></div>
      )}

      <div className="channel-upcoming-shift">
        {getChannelShiftTime(channel, activeChatWindowDays)}
      </div>
    </div>
  );
}
